/**
 * Moderator Ticket Detail Page
 * Extended ticket detail page with moderator actions, built to be worked one-handed on a phone:
 * the next step is a big button in a bar at the bottom, everything else is one "More" away, and
 * "Next" walks the filtered queue the moderator opened the ticket from.
 *
 * A failed action is a message, never a page swap: the ticket stays on screen and the error shows
 * in a snackbar. Only a ticket that cannot be loaded at all gets the error page. After a comment or
 * a claim decision the ticket refetches in the background, without a spinner or losing the scroll.
 */

import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material'
import {
  AddCircleOutline,
  ArrowBack,
  Block as CloseIcon,
  CheckCircle,
  DoNotDisturbOn as RejectIcon,
  DoneAll,
  HourglassBottom,
  NavigateBefore,
  NavigateNext,
  PlayArrow,
  Refresh as RefreshIcon,
  RemoveCircleOutline,
  Warning as EscalateIcon,
} from '@mui/icons-material'
import {
  getTicketDetails,
  addComment,
  claimTicket,
  unclaimTicket,
  startProgress,
  markAwaitingUser,
  resolveTicket,
  reopenTicket,
  closeTicket,
  rejectTicket,
  escalateTicket,
  getRouteEvidence,
  approveOwnershipClaim,
  rejectOwnershipClaim,
  setOwnershipClaimReveal,
  queryTickets,
} from '@api/services/tickets.api'
import type { RouteReportEvidence } from '@api/types/tickets'
import {
  TicketDetailsDto,
  AddCommentRequest,
  TicketStatus,
  TicketTypeLabels,
  isValidationTicketType,
  isOwnershipClaimType,
} from '@api/types/tickets'
import { RouteEvidenceMap } from '../components/RouteEvidenceMap'
import { TicketReasonDialog } from '../components/TicketReasonDialog'
import { claimRevealDefault, getTicketActions, revealClaimantForDecision } from '../components/ticketActions'
import { readListContext, ticketListHref, ticketListQuery, type TicketListContext } from '../components/ticketListQuery'
import { TicketActionBar, type TicketBarAction } from '../components/TicketActionBar'
import { TicketEscalationChip } from '../components/TicketEscalationChip'
import { OwnershipClaimPanel } from '../components/OwnershipClaimPanel'
import { TicketStatusBadge } from '../components/TicketStatusBadge'
import { TicketPriorityIndicator } from '../components/TicketPriorityIndicator'
import { TicketTypeIcon } from '../components/TicketTypeIcon'
import { TicketCommentThread } from '../components/TicketCommentThread'
import { TicketPhotoGrid } from '../components/TicketPhotos'
import { TicketTargetLink } from '../components/TicketTargetLink'
import { TicketValidationChips } from '../components/TicketValidationChips'
import { useAuthStore } from '@store/authStore'
import { safeFormatDistanceToNow } from '@utils/dateUtils'
import { PageShell } from '@components/common/PageShell'
import { ConfirmDialog } from '@components/common/ConfirmDialog'

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback

/** How many more ids to fetch when "Next" runs off the end of the list's snapshot. */
const NEIGHBOUR_WINDOW = 25

type ReasonDialog = 'resolve' | 'resolveClose' | 'reject' | 'escalate' | null

interface Notice {
  severity: 'success' | 'error' | 'info'
  text: string
  /** Offer "Next ticket" on the message (after a ticket is finished). */
  offerNext?: boolean
}

/** Remounted per ticket (keyed below) so dialogs, notices and evidence never leak to the next one. */
export default function ModeratorTicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>()
  return <TicketDetail key={ticketId} ticketId={ticketId ?? ''} />
}

function TicketDetail({ ticketId }: { ticketId: string }) {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const listContext = readListContext(location)

  const queryKey = ['tickets', 'detail', ticketId]
  const {
    data: ticket,
    isLoading,
    isFetching,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => getTicketDetails(ticketId),
    enabled: !!ticketId,
  })
  const setTicket = (next: TicketDetailsDto) => queryClient.setQueryData(queryKey, next)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [reasonDialog, setReasonDialog] = useState<ReasonDialog>(null)
  const [confirmClose, setConfirmClose] = useState(false)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [stepping, setStepping] = useState(false)
  /**
   * Route evidence, when this ticket has any. Fetched on its own rather than with the ticket
   * because a track is thousands of points, and most tickets are not route reports at all.
   */
  const [evidence, setEvidence] = useState<RouteReportEvidence | null>(null)

  useEffect(() => {
    if (!ticketId) return
    let cancelled = false
    getRouteEvidence(ticketId)
      .then((data) => {
        if (!cancelled) setEvidence(data)
      })
      // Missing evidence is the ordinary case and already returns null; anything reaching here is
      // a real failure, and it must not take the rest of the ticket down with it.
      .catch((err) => console.error('Failed to load route evidence:', err))
    return () => {
      cancelled = true
    }
  }, [ticketId])

  // ---- Queue navigation -------------------------------------------------------------------

  const index = listContext ? listContext.ids.indexOf(ticketId) : -1
  const inList = !!listContext && index >= 0
  const hasPrev = inList && (index > 0 || listContext.skip > 0)
  const hasNext = inList && (index < listContext.ids.length - 1 || listContext.skip + listContext.ids.length < listContext.total)

  const goTo = (id: string, ctx: TicketListContext) =>
    // replace: the browser's Back leaves the ticket for the list, not through every ticket stepped past.
    navigate(`/tickets/${id}`, { replace: true, state: { ticketList: ctx } })

  /**
   * Step through the list snapshot; past its end, fetch the next window of the same query. Ids
   * already in the snapshot are skipped, because resolving tickets shifts the server's offsets.
   */
  const step = async (direction: 1 | -1) => {
    if (!listContext || index < 0) return
    const neighbour = listContext.ids[index + direction]
    if (neighbour) {
      goTo(neighbour, listContext)
      return
    }
    const base = ticketListQuery(new URLSearchParams(listContext.search))
    setStepping(true)
    try {
      if (direction === 1) {
        const page = await queryTickets({ ...base, skip: listContext.skip + listContext.ids.length, take: NEIGHBOUR_WINDOW })
        const fresh = page.items.map((t) => t.id).filter((id) => !listContext.ids.includes(id))
        if (fresh.length === 0) {
          setNotice({ severity: 'info', text: 'That was the last ticket in this list.' })
          return
        }
        goTo(fresh[0], { ...listContext, ids: [...listContext.ids, ...fresh], total: page.totalCount })
      } else {
        const skip = Math.max(0, listContext.skip - NEIGHBOUR_WINDOW)
        const page = await queryTickets({ ...base, skip, take: listContext.skip - skip })
        const fresh = page.items.map((t) => t.id).filter((id) => !listContext.ids.includes(id))
        if (fresh.length === 0) {
          setNotice({ severity: 'info', text: 'That was the first ticket in this list.' })
          return
        }
        goTo(fresh[fresh.length - 1], { ...listContext, ids: [...fresh, ...listContext.ids], skip, total: page.totalCount })
      }
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not load the next ticket') })
    } finally {
      setStepping(false)
    }
  }
  const goNext = () => step(1)
  const goPrev = () => step(-1)

  const handleBack = () => navigate(ticketListHref(listContext))

  // ---- Actions ----------------------------------------------------------------------------

  /**
   * Runs one moderator action. Success replaces the cached ticket (and the list refetches next
   * time it is shown); failure is a message, the ticket stays on screen.
   */
  const run = async (
    fallback: string,
    action: () => Promise<TicketDetailsDto | void>,
    done?: Notice
  ): Promise<boolean> => {
    setIsSubmitting(true)
    try {
      const updated = await action()
      if (updated) setTicket(updated)
      else await refetch()
      queryClient.invalidateQueries({ queryKey: ['tickets', 'moderator-list'] })
      queryClient.invalidateQueries({ queryKey: ['tickets', 'moderator-count'] })
      if (done) setNotice(done)
      return true
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, fallback) })
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const finished = (text: string): Notice => ({ severity: 'success', text, offerNext: hasNext })

  const handleAddComment = async (request: AddCommentRequest) => {
    setIsSubmitting(true)
    try {
      await addComment(ticketId, request)
      // Background: the thread updates in place, no spinner, scroll kept.
      await refetch()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClaim = () => run('Failed to claim ticket', () => claimTicket(ticketId), { severity: 'success', text: 'Ticket claimed' })
  const handleUnclaim = () => run('Failed to unclaim ticket', () => unclaimTicket(ticketId), { severity: 'success', text: 'Returned to the pool' })
  const handleStartProgress = () => run('Failed to start progress', () => startProgress(ticketId))
  const handleMarkAwaitingUser = () => run('Failed to mark as awaiting user', () => markAwaitingUser(ticketId))
  const handleReopen = () => run('Failed to reopen ticket', () => reopenTicket(ticketId), { severity: 'success', text: 'Ticket reopened' })

  const handleResolve = async (notes: string) => {
    const ok = await run('Failed to resolve ticket', () => resolveTicket(ticketId, { resolutionNotes: notes }), finished('Ticket resolved'))
    if (ok) setReasonDialog(null)
  }

  /**
   * Resolve, then Close: the two steps a finished ticket always took, as one. If the close fails the
   * ticket is still resolved, and the message says so rather than pretending nothing happened.
   */
  const handleResolveAndClose = async (notes: string) => {
    setIsSubmitting(true)
    try {
      const resolved = await resolveTicket(ticketId, { resolutionNotes: notes })
      setTicket(resolved)
      setReasonDialog(null)
      try {
        setTicket(await closeTicket(ticketId))
        setNotice(finished('Ticket resolved and closed'))
      } catch (err) {
        setNotice({ severity: 'error', text: `Resolved, but closing failed: ${errorMessage(err, 'unknown error')}` })
      }
      queryClient.invalidateQueries({ queryKey: ['tickets', 'moderator-list'] })
      queryClient.invalidateQueries({ queryKey: ['tickets', 'moderator-count'] })
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Failed to resolve ticket') })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReject = async (reason: string) => {
    if (!reason) return
    const ok = await run('Failed to reject ticket', () => rejectTicket(ticketId, reason), finished('Ticket rejected'))
    if (ok) setReasonDialog(null)
  }

  const handleClose = async () => {
    const ok = await run('Failed to close ticket', () => closeTicket(ticketId), finished('Ticket closed'))
    if (ok) setConfirmClose(false)
  }

  const handleEscalate = async (reason: string) => {
    if (!reason) return
    const ok = await run('Failed to escalate ticket', () => escalateTicket(ticketId, { reason }), { severity: 'success', text: 'Ticket escalated' })
    if (ok) setReasonDialog(null)
  }

  const handleApproveClaim = (notes: string, keepMaintainers: boolean, reveal: boolean) => {
    if (!ticket) return
    return run(
      'Failed to approve the claim',
      async () => {
        await approveOwnershipClaim(ticketId, {
          notes: notes || undefined,
          keepMaintainers,
          revealClaimant: revealClaimantForDecision(claimRevealDefault(ticket), reveal),
        })
      },
      finished('Claim approved')
    )
  }

  const handleRejectClaim = (reason: string, reveal: boolean) => {
    if (!reason || !ticket) return
    return run(
      'Failed to reject the claim',
      async () => {
        await rejectOwnershipClaim(ticketId, reason, revealClaimantForDecision(claimRevealDefault(ticket), reveal))
      },
      finished('Claim rejected')
    )
  }

  const handleRevealClaimant = (reveal: boolean) =>
    run('Failed to change who the owner is told about', () => setOwnershipClaimReveal(ticketId, reveal))

  // ---- Render -----------------------------------------------------------------------------

  const queueNav = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Button variant="text" startIcon={<ArrowBack />} onClick={handleBack} sx={{ minHeight: 40, ml: -1 }}>
        Tickets
      </Button>
      {inList && (
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <IconButton aria-label="Previous ticket" onClick={goPrev} disabled={!hasPrev || stepping} sx={{ width: 40, height: 40 }}>
            <NavigateBefore />
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 64, textAlign: 'center' }}>
            {listContext.skip + index + 1} of {listContext.total}
          </Typography>
          <IconButton aria-label="Next ticket" onClick={goNext} disabled={!hasNext || stepping} sx={{ width: 40, height: 40 }}>
            {stepping ? <CircularProgress size={18} /> : <NavigateNext />}
          </IconButton>
        </Box>
      )}
    </Box>
  )

  if (isLoading) {
    return (
      <PageShell title="Ticket" breadcrumbs={queueNav}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    )
  }

  if (loadError || !ticket) {
    return (
      <PageShell title="Ticket" breadcrumbs={queueNav}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {loadError ? errorMessage(loadError, 'Failed to load ticket') : 'Ticket not found'}
        </Alert>
        {hasNext && (
          <Button variant="outlined" endIcon={<NavigateNext />} onClick={goNext}>
            Next ticket
          </Button>
        )}
      </PageShell>
    )
  }

  const {
    claimedByMe,
    claimedByOther,
    canClaim,
    canUnclaim,
    canStartProgress,
    canMarkAwaiting,
    canResolve,
    canReject,
    canClose,
    canReopen,
    canEscalate,
    canDecideClaim,
  } = getTicketActions(ticket, user?.userId, user?.roles)
  const isClaim = isOwnershipClaimType(ticket.type)
  const isDone = ticket.status === TicketStatus.Closed || ticket.status === TicketStatus.Resolved

  // In order of "what would the moderator do next": the first non-destructive one is the big
  // button on a phone. Resolve & close is the usual finish; plain Resolve stays for a ticket that
  // should wait for the reporter to confirm.
  const barActions: TicketBarAction[] = []
  if (canClaim) barActions.push({ key: 'claim', label: 'Claim ticket', icon: <AddCircleOutline />, onClick: handleClaim })
  if (canResolve && canClose)
    barActions.push({
      key: 'resolveClose',
      label: 'Resolve & close',
      icon: <DoneAll />,
      color: 'success',
      onClick: () => setReasonDialog('resolveClose'),
    })
  if (canResolve)
    barActions.push({
      key: 'resolve',
      label: canClose ? 'Resolve only' : 'Resolve',
      hint: canClose ? 'Leave it open for a Close later' : undefined,
      icon: <CheckCircle />,
      color: 'success',
      onClick: () => setReasonDialog('resolve'),
    })
  if (canStartProgress) barActions.push({ key: 'start', label: 'Start progress', icon: <PlayArrow />, onClick: handleStartProgress })
  if (canMarkAwaiting) barActions.push({ key: 'await', label: 'Await user', icon: <HourglassBottom />, onClick: handleMarkAwaitingUser })
  if (canClose)
    barActions.push({
      key: 'close',
      label: ticket.status === TicketStatus.Resolved ? 'Close' : 'Close without resolving',
      icon: <CloseIcon />,
      color: 'info',
      onClick: () => setConfirmClose(true),
    })
  if (canReopen) barActions.push({ key: 'reopen', label: 'Reopen', icon: <RefreshIcon />, color: 'warning', onClick: handleReopen })
  if (canUnclaim) barActions.push({ key: 'unclaim', label: 'Unclaim', icon: <RemoveCircleOutline />, onClick: handleUnclaim })
  if (canEscalate)
    barActions.push({ key: 'escalate', label: 'Escalate', icon: <EscalateIcon />, color: 'error', onClick: () => setReasonDialog('escalate') })
  if (canReject)
    barActions.push({
      key: 'reject',
      label: 'Reject',
      hint: 'Invalid, spam or duplicate',
      icon: <RejectIcon />,
      color: 'error',
      onClick: () => setReasonDialog('reject'),
    })
  // A finished ticket's next step is the next ticket.
  if (isDone && hasNext) barActions.unshift({ key: 'next', label: 'Next ticket', icon: <NavigateNext />, onClick: goNext })

  const votes = (ticket.upVotes ?? 0) - (ticket.downVotes ?? 0)

  return (
    <PageShell
      title={ticket.title}
      subtitle={`${TicketTypeLabels[ticket.type] ?? 'Ticket'} · #${ticket.id.slice(0, 8)}`}
      breadcrumbs={queueNav}
    >
      {/* Background refresh (after a comment or a claim decision): a hairline, not a spinner. */}
      <Box sx={{ height: 2, mb: 1, mt: -1 }}>{isFetching && <LinearProgress sx={{ height: 2 }} />}</Box>

      {/* Status */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        <TicketTypeIcon type={ticket.type} size="small" showLabel />
        <TicketStatusBadge status={ticket.status} />
        <TicketPriorityIndicator priority={ticket.priority} />
        {ticket.isEscalated && <Chip label="ESCALATED" color="error" size="small" />}
        <TicketEscalationChip level={ticket.escalationLevel} label={ticket.escalationLabel} paused={ticket.escalationPaused} />
        {ticket.isCommunity && (
          <Chip
            size="small"
            variant="outlined"
            label={`Votes ${votes > 0 ? '+' : ''}${votes} (${ticket.upVotes ?? 0} up / ${ticket.downVotes ?? 0} down)`}
          />
        )}
        <TicketValidationChips type={ticket.type} itemKey={ticket.itemKey} />
      </Box>

      <Typography variant="body2" color="text.secondary">
        Created {safeFormatDistanceToNow(ticket.createdAt)} by <strong>{ticket.createdByDisplayName || 'Unknown'}</strong>
      </Typography>
      {ticket.escalationLevelChangedAt && (
        <Typography variant="body2" color="text.secondary">
          Escalated to {ticket.escalationLabel || 'moderators'} {safeFormatDistanceToNow(ticket.escalationLevelChangedAt)}
        </Typography>
      )}

      {/* Moderator actions */}
      <Box sx={{ mt: 2 }}>
        {claimedByMe && !isDone && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {ticket.status === TicketStatus.Claimed && "Claimed by you. 'Start progress' when you begin, or finish it straight away."}
            {ticket.status === TicketStatus.InProgress && "In progress. 'Await user' if you need the reporter, or finish it when fixed."}
            {ticket.status === TicketStatus.AwaitingUser && 'Waiting on the reporter. Finish it when ready.'}
          </Alert>
        )}
        {claimedByMe && ticket.status === TicketStatus.Resolved && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Resolved. 'Close' to confirm completion.
          </Alert>
        )}
        {isClaim && canDecideClaim && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Ownership claims are settled with Approve claim / Reject claim below.
          </Alert>
        )}
        {barActions.length === 0 ? (
          <Alert severity="warning">
            {claimedByOther ? (
              <>
                This ticket is being handled by <strong>{ticket.claimedByDisplayName}</strong>. Only the assigned moderator can
                take actions on claimed tickets.
              </>
            ) : (
              <>No actions available for this ticket in its current state.</>
            )}
          </Alert>
        ) : (
          // Desktop: the row lives here. Phone: the bar pins to the bottom, rendered at the end.
          <TicketActionBar placement="inline" actions={barActions} busy={isSubmitting} />
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Description: an ownership claim shows its statement in the claim panel instead. */}
      {isClaim ? (
        <OwnershipClaimPanel
          ticket={ticket}
          canDecide={canDecideClaim}
          submitting={isSubmitting}
          onApprove={handleApproveClaim}
          onReject={handleRejectClaim}
          onReveal={handleRevealClaimant}
        />
      ) : (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Description
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {ticket.description}
          </Typography>
          <TicketPhotoGrid attachments={ticket.attachments} />
        </Box>
      )}

      {/* The reported route beside what the reporter actually walked. Only rendered when the
          evidence exists, so an ordinary ticket looks exactly as it did. */}
      {evidence && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Route evidence
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {evidence.adventureTitle}
          </Typography>
          <RouteEvidenceMap evidence={evidence} />
        </Box>
      )}

      {ticket.tags && ticket.tags.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Tags
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {ticket.tags.map((tag: string | { id: string; displayName?: string; name?: string }) => {
              const tagId = typeof tag === 'string' ? tag : tag.id
              const tagLabel = typeof tag === 'string' ? tag : tag.displayName || tag.name
              return <Chip key={tagId} label={tagLabel} size="small" />
            })}
          </Box>
        </Box>
      )}

      {ticket.targetType !== null && ticket.targetId !== null && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {isValidationTicketType(ticket.type) ? 'Adventure' : 'Reported Content'}
          </Typography>
          <TicketTargetLink targetType={ticket.targetType} targetId={ticket.targetId} variant="chip" />
        </Box>
      )}

      <Divider sx={{ my: 2 }} />
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        <Grid item xs={6} md={4}>
          <Typography variant="caption" color="text.secondary">
            Ticket ID
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {ticket.id.slice(0, 8)}...
          </Typography>
        </Grid>
        {ticket.claimedByDisplayName && (
          <Grid item xs={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              Claimed By
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{ticket.claimedByDisplayName}</Typography>
          </Grid>
        )}
        {ticket.assignedToDisplayName && (
          <Grid item xs={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              Assigned To
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{ticket.assignedToDisplayName}</Typography>
          </Grid>
        )}
        {ticket.resolvedByDisplayName && (
          <Grid item xs={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              Resolved By
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{ticket.resolvedByDisplayName}</Typography>
          </Grid>
        )}
        <Grid item xs={6} md={4}>
          <Typography variant="caption" color="text.secondary">
            Last Updated
          </Typography>
          <Typography variant="body2">{safeFormatDistanceToNow(ticket.lastUpdatedAt)}</Typography>
        </Grid>
      </Grid>

      {ticket.resolutionNotes && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Resolution Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {ticket.resolutionNotes}
          </Typography>
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      <TicketCommentThread
        comments={ticket.comments}
        onAddComment={handleAddComment}
        canAddInternalComments={true}
        isSubmitting={isSubmitting}
        showParty={isClaim}
      />

      {/* Phone: the action bar, pinned to the bottom of the scrolling page. */}
      <TicketActionBar
        placement="bottom"
        actions={barActions}
        busy={isSubmitting || stepping}
        onNext={hasNext && barActions[0]?.key !== 'next' ? goNext : undefined}
      />

      <TicketReasonDialog
        open={reasonDialog === 'resolve' || reasonDialog === 'resolveClose'}
        title={reasonDialog === 'resolveClose' ? 'Resolve and close ticket' : 'Resolve ticket'}
        description={
          reasonDialog === 'resolveClose'
            ? 'Say what was done about it. The reporter sees these notes. The ticket is closed straight after; it can be reopened later.'
            : 'Say what was done about it. The reporter sees these notes.'
        }
        label="Resolution notes"
        confirmLabel={reasonDialog === 'resolveClose' ? 'Resolve & close' : 'Resolve'}
        confirmColor="success"
        required
        submitting={isSubmitting}
        onCancel={() => setReasonDialog(null)}
        onConfirm={reasonDialog === 'resolveClose' ? handleResolveAndClose : handleResolve}
      />
      <TicketReasonDialog
        open={reasonDialog === 'reject'}
        title="Reject ticket"
        description="Close this ticket as invalid, spam or a duplicate. The reporter is told it was closed, with this reason. It can be reopened later."
        label="Reason"
        confirmLabel="Reject"
        confirmColor="error"
        required
        submitting={isSubmitting}
        onCancel={() => setReasonDialog(null)}
        onConfirm={handleReject}
      />
      <TicketReasonDialog
        open={reasonDialog === 'escalate'}
        title="Escalate ticket"
        description="Hand this ticket up to the site moderators. Say why, so whoever picks it up knows where you got to."
        label="Escalation reason"
        confirmLabel="Escalate"
        confirmColor="error"
        required
        submitting={isSubmitting}
        onCancel={() => setReasonDialog(null)}
        onConfirm={handleEscalate}
      />
      <ConfirmDialog
        open={confirmClose}
        title={ticket.status === TicketStatus.Resolved ? 'Close ticket?' : 'Close without resolving?'}
        message={
          ticket.status === TicketStatus.Resolved
            ? 'This confirms the ticket is finished. It can be reopened if needed.'
            : 'The ticket is closed with no resolution notes. Use Resolve & close to tell the reporter what was done. It can be reopened if needed.'
        }
        confirmLabel="Close ticket"
        loading={isSubmitting}
        onConfirm={handleClose}
        onClose={() => setConfirmClose(false)}
      />

      <Snackbar
        open={!!notice}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') setNotice(null)
        }}
        // Errors stay until dismissed; a success note goes on its own.
        autoHideDuration={notice?.severity === 'error' ? null : 6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // Clear of the phone action bar.
        sx={{ bottom: { xs: 'calc(80px + env(safe-area-inset-bottom))', sm: 24 } }}
      >
        {notice ? (
          <Alert
            severity={notice.severity}
            variant="filled"
            onClose={() => setNotice(null)}
            sx={{ width: '100%', alignItems: 'center' }}
            action={
              notice.offerNext && hasNext ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setNotice(null)
                    goNext()
                  }}
                >
                  Next ticket
                </Button>
              ) : undefined
            }
          >
            {notice.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </PageShell>
  )
}
